import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";
import dropDown from "../../assets/images/dropdownDown.svg";
import CheckBoxChecked from "../../assets/images/checkBoxChecked.svg";
import CheckBox from "../../assets/images/Checkbox.svg";
import CheckBoxUnchecked from "../../assets/images/checkboxUnchecked.svg";
import searchIcon from "../../assets/images/search-icon.svg"; // adjust path as needed
import { X } from "lucide-react";
// Adjust the path as necessary
type MultiSelectProps = {
  options: string[];
  selected: string[];
  setSelected: (value: string[]) => void;
  placeholder?: string;
  closeOnClickOutside?: boolean; // Add this prop
};

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  setSelected,
  placeholder = "Search...",
  closeOnClickOutside = false, // Default to false for backward compatibility
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Add click outside logic
  React.useEffect(() => {
    if (!closeOnClickOutside || !open) return;
    const handleClick = (event: MouseEvent) => {
      const dropdown = document.getElementById("multi-select-dropdown");
      if (dropdown && !dropdown.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [closeOnClickOutside, open]);

  const filtered = options.filter((item) =>
    item.toLowerCase().includes(search.toLowerCase()),
  );

  // Select-all indicator logic
  const allSelected = selected.length === options.length && options.length > 0;
  const someSelected = selected.length > 0 && selected.length < options.length;

  const toggle = (option: string) => {
    setSelected(
      selected.includes(option)
        ? selected.filter((o) => o !== option)
        : [...selected, option],
    );
  };

  const remove = (option: string) => {
    setSelected(selected.filter((o) => o !== option));
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(options);
    }
  };

     const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filtered.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filtered.length - 1
      );
    } else if (e.key === "Enter" || e.key === " ") {
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        toggle(filtered[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };
   useEffect(() => {
    if (open) {
      setHighlightedIndex(filtered.length > 0 ? 0 : -1);
    } else {
      setHighlightedIndex(-1);
    }
  }, [open, search, filtered.length]);

  // Focus input when dropdown opens
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);
   useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className={styles.container} id="multi-select-dropdown">
      <div
        onClick={() => setOpen((prev) => !prev)}
        className={styles.selectedChips}
         tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className={styles.selectedContent}>
          {selected.length === 0 ? (
            <span className={styles.placeholderText}></span>
          ) : selected.length > 2 ? (
            <span className={styles.selectedCountText}>
              {selected.length} cities selected
            </span>
          ) : (
            <>
              {selected.map((item) => (
                <span className={styles.chip} key={item}>
                  {item}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(item);
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
        </div>
        <span className={styles.downArrow}>
          <img src={dropDown} alt="dropdown" />
        </span>
      </div>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchBar}>
            {/* Select-all indicator */}
            <span
              className={styles.selectAllIndicator}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
              title={allSelected ? "Deselect all" : "Select all"}
              style={{ cursor: "pointer", marginRight: 8 }}
            >
              <img
                src={CheckBox}
                alt={allSelected ? "Deselect all" : "Select all"}
                className={styles.customCheckbox}
                style={{ verticalAlign: "middle" }}
              />
            </span>

            <div className={styles.inputWrapper}>
              {!search && (
                <img
                  src={searchIcon}
                  alt="search"
                  className={styles.searchIcon}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
              <input
              ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.searchInput}
                onKeyDown={handleKeyDown}
                // placeholder={placeholder}
                style={{
                  paddingLeft: !search ? 32 : undefined,
                }}
              />
            </div>

            {/* Cross icon to close dropdown */}
            <X
              className={styles.closeDropdown}
              onClick={() => {
                setOpen(false);
                setSearch("");
              }}
              size={20}
              // style={{ marginLeft: 8 }}
              aria-label="Close dropdown"
            >
              
            </X>
          </div>

          <ul className={styles.options} role="listbox">
            {filtered.map((opt, idx) => (
              <li
                key={opt}
                className={`${styles.option} ${
                  highlightedIndex === idx ? styles.highlighted : ""
                }`}
                onClick={() => toggle(opt)}
                style={{
                  cursor: "pointer",
                  background:
                    highlightedIndex === idx ? "#f0f0f0" : undefined,
                }}
                role="option"
                aria-selected={selected.includes(opt)}
                tabIndex={-1}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={selected.includes(opt)}
                  style={{ display: "none" }}
                />
                <img
                  src={
                    selected.includes(opt) ? CheckBoxChecked : CheckBoxUnchecked
                  }
                  alt={selected.includes(opt) ? "Checked" : "Unchecked"}
                  className={styles.customCheckbox}
                  style={{ verticalAlign: "middle" }}
                />
                <label style={{ marginLeft: 8, cursor: "pointer" }}>
                  {opt}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
